import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { BaseButton, IMemberPlan } from "../../libs";
import { paymentService } from "../../services";
import SearchIcon from "../../assets/images/icons/search.svg";
import { Loader } from "../ui";
import { ReferralProgramCard } from "./referral-program-card";
import { ReferralProgramModal } from "./referral-program-modal";
import { ReferralParticipantsPanel } from "./referral-participants-panel/ReferralParticipantsPanel";
import { ReferralParticipant, ReferralProgram } from "./types";
import * as Styled from "./ManageReferralProgramsComponent.styled";

interface CouponOption {
  id: string;
  code: string;
  name: string;
  active: boolean;
}
const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;

export const ManageReferralProgramsComponent: React.FC = () => {
  const [programs, setPrograms] = useState<ReferralProgram[]>([]);
  const [participants, setParticipants] = useState<Record<string, ReferralParticipant[]>>({});
  const [plans, setPlans] = useState<IMemberPlan[]>([]);
  const [coupons, setCoupons] = useState<CouponOption[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingProgram, setEditingProgram] = useState<ReferralProgram | null>(null);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<ReferralProgram | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [programData, planData, couponData] = await Promise.all([
        paymentService.getReferralPrograms(),
        paymentService.getCustomerProductPlans(),
        paymentService.getCoupons(),
      ]);
      setPrograms(programData);
      setSelectedProgram((current) =>
        current
          ? programData.find((program: ReferralProgram) => program.id === current.id) || current
          : current
      );
      setPlans(planData);
      setCoupons(couponData);
      const rows = await Promise.all(
        programData.map(
          async (program: ReferralProgram) =>
            [program.id, await paymentService.getReferralParticipants(program.id)] as const
        )
      );
      setParticipants(Object.fromEntries(rows));
    } catch (error) {
      console.error("Error loading referral programs:", error);
      toast.error(errorMessage(error, "Failed to load referral programs"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredPrograms = useMemo(
    () =>
      programs.filter((program) =>
        `${program.name} ${program.description}`.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [programs, searchQuery]
  );
  const allParticipants = Object.values(participants).flat();
  const registrations = allParticipants.reduce(
    (total, participant) => total + (participant.registrationsCount || 0),
    0
  );
  const purchases = allParticipants.reduce(
    (total, participant) => total + (participant.purchasesCount || 0),
    0
  );

  const saveProgram = async (values: Record<string, unknown>) => {
    try {
      if (editingProgram) await paymentService.updateReferralProgram(editingProgram.id, values);
      else await paymentService.createReferralProgram(values);
      toast.success(editingProgram ? "Referral program updated" : "Referral program created");
      await fetchData();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save referral program"));
      throw error;
    }
  };

  const invite = async (data: { email: string; invitationUrl?: string }) => {
    if (!selectedProgram) return;
    try {
      const invited = await paymentService.inviteReferralParticipant(selectedProgram.id, data);
      setParticipants((current) => ({
        ...current,
        [selectedProgram.id]: [...(current[selectedProgram.id] || []), invited],
      }));
      if (invited.invitationSent === false)
        toast.warning(invited.invitationError || "Referrer added, but the email could not be sent");
      else toast.success("Referrer added and invitation sent");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to add referrer"));
      throw error;
    }
  };

  const addDestination = async (url: string) => {
    if (!selectedProgram) return;
    try {
      const updated = await paymentService.updateReferralProgram(selectedProgram.id, {
        destinationUrls: [...(selectedProgram.destinationUrls || []), url],
      });
      setPrograms((current) =>
        current.map((program) => (program.id === updated.id ? { ...program, ...updated } : program))
      );
      setSelectedProgram((current) => (current ? { ...current, ...updated } : current));
      toast.success("Destination URL added");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to add destination URL"));
      throw error;
    }
  };

  const getParticipantActivity = (participantId: string) =>
    paymentService.getReferralParticipantActivity(participantId);

  const addParticipantNote = async (participantId: string, text: string) => {
    try {
      const note = await paymentService.addReferralParticipantNote(participantId, text);
      setParticipants((current) =>
        Object.fromEntries(
          Object.entries(current).map(([programId, rows]) => [
            programId,
            rows.map((participant) =>
              participant.id === participantId
                ? { ...participant, notes: [...(participant.notes || []), note] }
                : participant
            ),
          ])
        )
      );
      toast.success("Note added");
      return note;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to add note"));
      throw error;
    }
  };

  const deleteParticipantNote = async (participantId: string, noteId: string) => {
    try {
      await paymentService.deleteReferralParticipantNote(participantId, noteId);
      setParticipants((current) =>
        Object.fromEntries(
          Object.entries(current).map(([programId, rows]) => [
            programId,
            rows.map((participant) =>
              participant.id === participantId
                ? {
                    ...participant,
                    notes: (participant.notes || []).filter((note) => note.id !== noteId),
                  }
                : participant
            ),
          ])
        )
      );
      toast.success("Note removed");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to remove note"));
      throw error;
    }
  };

  if (loading) return <Loader />;
  return (
    <Styled.StyledContainer>
      <Styled.ContentWrapper $showDetails={!!selectedProgram}>
        <Styled.ProgramsSection $showDetails={!!selectedProgram}>
          <Styled.Header>
            <Styled.HeaderLeftSection>Referral Programs</Styled.HeaderLeftSection>
            <Styled.HeaderRightSection>
              <Styled.SearchBarWrapper>
                <Styled.SearchIcon src={SearchIcon} alt="Search" />
                <Styled.SearchInput
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search programs"
                />
              </Styled.SearchBarWrapper>
              <BaseButton
                onClick={() => {
                  setEditingProgram(null);
                  setShowProgramModal(true);
                }}
              >
                <Styled.NewButtonContent>
                  <Plus size={26} /> New Program
                </Styled.NewButtonContent>
              </BaseButton>
            </Styled.HeaderRightSection>
          </Styled.Header>
          <Styled.Description>
            Create public or invitation-only referral programs, choose attribution rules, plans,
            destinations, and optional automatic coupons.
          </Styled.Description>
          <Styled.StatsGrid>
            <Styled.StatCard>
              <Styled.StatValue>
                {programs.filter((program) => program.status === "ACTIVE").length}
              </Styled.StatValue>
              <Styled.StatLabel>Active programs</Styled.StatLabel>
            </Styled.StatCard>
            <Styled.StatCard>
              <Styled.StatValue>{allParticipants.length}</Styled.StatValue>
              <Styled.StatLabel>Referrers</Styled.StatLabel>
            </Styled.StatCard>
            <Styled.StatCard>
              <Styled.StatValue>{registrations}</Styled.StatValue>
              <Styled.StatLabel>Registrations</Styled.StatLabel>
            </Styled.StatCard>
            <Styled.StatCard>
              <Styled.StatValue>{purchases}</Styled.StatValue>
              <Styled.StatLabel>Purchases</Styled.StatLabel>
            </Styled.StatCard>
          </Styled.StatsGrid>
          <Styled.List>
            {filteredPrograms.length ? (
              filteredPrograms.map((program) => (
                <ReferralProgramCard
                  key={program.id}
                  program={program}
                  participants={participants[program.id] || []}
                  onEdit={() => {
                    setEditingProgram(program);
                    setShowProgramModal(true);
                  }}
                  onViewParticipants={() => setSelectedProgram(program)}
                />
              ))
            ) : (
              <Styled.NoDataWrapper>
                No referral programs found. Create a program to get started.
              </Styled.NoDataWrapper>
            )}
          </Styled.List>
          <ReferralProgramModal
            show={showProgramModal}
            onHide={() => setShowProgramModal(false)}
            program={editingProgram}
            plans={plans}
            coupons={coupons}
            onSave={saveProgram}
          />
        </Styled.ProgramsSection>
        <Styled.DetailsSection $showDetails={!!selectedProgram}>
          {selectedProgram && (
            <ReferralParticipantsPanel
              program={selectedProgram}
              participants={participants[selectedProgram.id] || []}
              onBack={() => setSelectedProgram(null)}
              onEdit={() => {
                setEditingProgram(selectedProgram);
                setShowProgramModal(true);
              }}
              onInvite={invite}
              onAddDestination={addDestination}
              onGetActivity={getParticipantActivity}
              onAddNote={addParticipantNote}
              onDeleteNote={deleteParticipantNote}
            />
          )}
        </Styled.DetailsSection>
      </Styled.ContentWrapper>
    </Styled.StyledContainer>
  );
};
